// Editor «Detalle por líneas» de una nota de crédito/débito de Farmacia, sobre una factura que mezcla tarifas de IVA.
//
// COPIA de packages/web/src/api/lib/nota-lineas.ts (la imagen de este paquete no incluye packages/web, así que no puede importarlo).
// La cuenta replica el motor único (packages/web/src/api/lib/impuestos.ts, `calcularDocumentoLegado`): base de cada línea a partir del
// total con IVA, subtotal por tarifa, IVA redondeado UNA vez por subtotal por tarifa, total; tolerancia cero. `notaLineas.test.ts` ata esta
// copia a la fuente y al motor con casos reales y un barrido: si el motor o la fuente cambian, ese test falla.

/** Mismos topes que el servidor (lib/notas-credito.ts, `validarLineasDeNota`) y el esquema de la ruta. */
export const CANTIDAD_MAX = 999;
export const DESCRIPCION_MAX = 300;

export type LineaFacturaParaNota = { itemId: number | null; description: string; quantity: number; unitAmountCents: number; ivaRate: number };

export type FilaNota = {
  /** Ítem de catálogo de la línea de la factura (el servidor lo usa para empatarla y reintegrar su stock); `null` = línea libre. */
  itemId: number | null;
  description: string;
  /** Texto: entero ≥ 0. 0 o vacío = la línea no entra en la nota. */
  cantidad: string;
  /** Texto en USD, CON IVA incluido, hasta 2 decimales. */
  precio: string;
  ivaRate: number;
  /** Lo facturado en esta línea (precio × cantidad, con IVA): tope de la fila en una nota de crédito. */
  maxCents: number;
};

export type LineaNota = { itemId: number | null; description: string; quantity: number; unitAmountCents: number; ivaRate: number };

export type ResultadoNota = {
  lines: LineaNota[];
  subtotalCents: number;
  ivaCents: number;
  totalCents: number;
  error: string | null;
};

/** round(a × b / c), mitad hacia arriba, exacto vía BigInt (igual que `mulDivRedondeado` del motor). */
function mulDivRedondeado(a: number, b: number, c: number): number {
  const num = BigInt(a) * BigInt(b);
  const den = BigInt(c);
  const q = (2n * num + den) / (2n * den);
  return Number(q);
}

/** Subtotal, IVA y total de líneas con IVA incluido (ruta de legado del motor). */
export function totalesDeLineas(lineas: readonly { totalConIvaCents: number; ivaRate: number }[]): { subtotalCents: number; ivaCents: number; totalCents: number } {
  const basePorTarifa = new Map<number, number>();
  for (const l of lineas) {
    const bp = Math.round(l.ivaRate * 100);
    const base = bp === 0 || l.totalConIvaCents <= 0 ? l.totalConIvaCents : mulDivRedondeado(l.totalConIvaCents, 10_000, 10_000 + bp);
    basePorTarifa.set(l.ivaRate, (basePorTarifa.get(l.ivaRate) ?? 0) + base);
  }
  let subtotalCents = 0;
  let ivaCents = 0;
  for (const [ivaRate, base] of basePorTarifa) {
    const bp = Math.round(ivaRate * 100);
    subtotalCents += base;
    ivaCents += bp === 0 || base <= 0 ? 0 : mulDivRedondeado(base, bp, 10_000);
  }
  return { subtotalCents, ivaCents, totalCents: subtotalCents + ivaCents };
}

export function dolaresACentavos(texto: string): number | null {
  const m = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(texto.trim());
  if (!m) return null;
  return Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
}

export function centavosADolares(cents: number): string {
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
}

/** Nota de crédito: la factura entera (se baja lo que no se acredita). Nota de débito: mismas descripciones y precios, cantidad 0. */
export function filasIniciales(lineas: readonly LineaFacturaParaNota[], tipo: 'nc' | 'nd'): FilaNota[] {
  return lineas.map((l) => ({
    itemId: l.itemId,
    description: l.description,
    cantidad: tipo === 'nc' ? String(l.quantity) : '0',
    precio: centavosADolares(l.unitAmountCents),
    ivaRate: l.ivaRate,
    maxCents: l.unitAmountCents * l.quantity,
  }));
}

/** `topeCents`: el saldo no acreditado (solo nota de crédito; `null` en la de débito). En una NC cada fila tampoco pasa de lo facturado en su línea. */
export function calcularNota(filas: readonly FilaNota[], tipo: 'nc' | 'nd', topeCents: number | null): ResultadoNota {
  let error: string | null = null;
  const lines: LineaNota[] = [];
  for (const f of filas) {
    const cantidadTxt = f.cantidad.trim();
    if (cantidadTxt === '' || cantidadTxt === '0') continue;
    if (!/^\d+$/.test(cantidadTxt)) {
      error ??= `La cantidad de «${f.description}» debe ser un número entero.`;
      continue;
    }
    const quantity = Number(cantidadTxt);
    if (quantity > CANTIDAD_MAX) {
      error ??= `La cantidad de «${f.description}» no puede pasar de ${CANTIDAD_MAX}.`;
      continue;
    }
    const unitAmountCents = dolaresACentavos(f.precio);
    if (unitAmountCents === null || unitAmountCents <= 0) {
      error ??= `Indique un precio válido (hasta 2 decimales) para «${f.description}».`;
      continue;
    }
    if (!f.description.trim() || f.description.length > DESCRIPCION_MAX) {
      error ??= `La descripción de cada línea debe tener entre 1 y ${DESCRIPCION_MAX} caracteres.`;
      continue;
    }
    if (tipo === 'nc' && unitAmountCents * quantity > f.maxCents) {
      error ??= `«${f.description}» no puede acreditar más de lo facturado en esa línea (USD ${centavosADolares(f.maxCents)}).`;
    }
    lines.push({ itemId: f.itemId, description: f.description.trim(), quantity, unitAmountCents, ivaRate: f.ivaRate });
  }
  const doc = totalesDeLineas(lines.map((l) => ({ totalConIvaCents: l.unitAmountCents * l.quantity, ivaRate: l.ivaRate })));
  if (lines.length === 0) error ??= 'Deje al menos una línea con cantidad.';
  else if (tipo === 'nc' && topeCents !== null && doc.totalCents > topeCents) {
    error ??= `El detalle (USD ${centavosADolares(doc.totalCents)}) supera el saldo disponible (USD ${centavosADolares(topeCents)}).`;
  }
  return { lines, ...doc, error };
}
